import { apiService } from 'core/services/apiService';
import { paginationService } from 'core/services/paginationService';
import { Partner, CommonPartner } from 'types';

/**
 * Partner Type from /partner_types endpoint
 */
export interface PartnerType {
	'@context': string;
	'@id': string;
	'@type': string;
	id: number;
	label: string;
}

/**
 * Toast restaurant/location from /common/toast/restaurants
 */
export interface ToastRestaurant {
	'@context'?: string;
	'@id'?: string;
	'@type'?: string;
	id: number;
	restaurant_guid: string;
	management_group_guid?: string;
	restaurant_name: string;
	location_name: string;
	external_group_ref?: string;
	external_restaurant_ref?: string;
	restaurant_phone_number?: string;
	restaurant_address_line1?: string;
	restaurant_address_line2?: string;
	restaurant_city?: string;
	restaurant_state?: string;
	restaurant_zip_code?: string;
	restaurant_country_code?: string;
	restaurant_timezone?: string;
	restaurant_latitude?: string;
	restaurant_longitude?: string;
	available?: boolean;
}

/** Build a single-line address from Toast restaurant fields */
export const formatToastRestaurantAddress = (restaurant: ToastRestaurant): string => {
	const line1 = restaurant.restaurant_address_line1?.trim();
	const line2 = restaurant.restaurant_address_line2?.trim();
	const city = restaurant.restaurant_city?.trim();
	const state = restaurant.restaurant_state?.trim();
	const zip = restaurant.restaurant_zip_code?.trim();
	const country = restaurant.restaurant_country_code?.trim();

	const cityStateZip = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
	return [line1, line2, cityStateZip, country].filter(Boolean).join(', ');
};

/**
 * Search Toast restaurants/locations accessible to BYOD.AI
 * GET /common/toast/restaurants?search=...
 */
export const searchToastRestaurants = async (search: string): Promise<ToastRestaurant[]> => {
	try {
		const trimmed = search.trim();
		if (!trimmed) {
			return [];
		}

		const response = await paginationService.getWithHandler(
			`/common/toast/restaurants?search=${encodeURIComponent(trimmed)}`,
			undefined,
			30
		);

		const restaurants: ToastRestaurant[] = response?.member || response?.items || [];
		return Array.isArray(restaurants) ? restaurants : [];
	} catch (error: any) {
		console.error('Failed to search Toast restaurants:', error);
		throw new Error(error.message || 'Failed to search Toast restaurants');
	}
};

/**
 * Get partner_lists parameters
 */
export interface GetPartnersParams {
	limit?: number;
	page?: number;
	active?: boolean;
}

/**
 * Raw partner_lists response from API
 */
export interface RawPartnerListResponse {
	'@context': string;
	'@id': string;
	'@type': string;
	id?: number;
	label: string;
	partnerId: number;
	type?: string;
	parameters?: any[];
	workplaces?: string[];
	active: boolean;
	deleted: boolean;
	created_at: string;
	updated_at: string;
}

/**
 * Get partner_lists response structure
 */
export interface GetPartnersResponse {
	member?: RawPartnerListResponse[];
	items?: RawPartnerListResponse[];
	total?: number;
	totalCount?: number;
}

/**
 * Create partner_list payload
 */
export interface CreatePartnerPayload {
	label: string;
	partnerId: number;
	type: string;
	/** Object of config keys (OAuth tokens, drive ids, etc.), or legacy array form */
	parameters?: Record<string, any> | any[];
	workplaces?: string[];
	/** Toast only: the single workplace this partner connects to (also saved on ODS) */
	workplaceId?: number;
	active: boolean;
	deleted?: boolean;
}

/**
 * Update partner_list payload
 */
export interface UpdatePartnerPayload {
	label: string;
	partnerId: number;
	type: string;
	/** Object of config keys (OAuth tokens, drive ids, etc.), or legacy array form */
	parameters?: Record<string, any> | any[];
	workplaces?: string[];
	active: boolean;
	deleted?: boolean;
	created_at?: string;
	updated_at?: string;
}

type IdResolvable = number | { id?: number; '@id'?: string } | undefined | null;

/**
 * Resolve numeric ID from an entity or raw id value, falling back to parsing @id IRI.
 * Backend may omit `id` but always provides `@id` (e.g. "/api/partner_lists/26").
 */
export const resolvePartnerId = (entity: IdResolvable): number => {
	if (typeof entity === 'number' && !Number.isNaN(entity)) {
		return entity;
	}
	if (!entity || typeof entity === 'number') {
		return 0;
	}
	if (typeof entity.id === 'number' && !Number.isNaN(entity.id)) {
		return entity.id;
	}
	if (typeof entity['@id'] === 'string') {
		const match = entity['@id'].match(/\/(\d+)$/);
		if (match) {
			return parseInt(match[1], 10);
		}
	}
	return 0;
};

/**
 * Transform raw API response to Partner type
 */
const transformPartnerListResponse = (raw: RawPartnerListResponse): Partner => {
	return {
		'@context': raw['@context'],
		'@id': raw['@id'],
		'@type': raw['@type'],
		id: resolvePartnerId(raw),
		label: raw.label,
		partnerId: raw.partnerId,
		type: raw.type,
		parameters: raw.parameters || [],
		workplaces: raw.workplaces || [],
		active: raw.active,
		deleted: raw.deleted,
		created_at: raw.created_at,
		updated_at: raw.updated_at,
	};
};

/**
 * Get all common partners from /common/partners
 */
export const getCommonPartners = async (): Promise<CommonPartner[]> => {
	try {
		const endpoint = '/common/partners';
		
		const response = await paginationService.getWithHandler(
			endpoint,
			undefined,
			100 // Get more items as common partners should be a relatively small list
		);

		const partnerList: CommonPartner[] = response?.member || response?.items || [];

		if (Array.isArray(partnerList)) {
			return partnerList;
		}

		return [];
	} catch (error: any) {
		console.error('Failed to fetch common partners:', error);
		throw new Error(error.message || 'Failed to fetch common partners');
	}
};

/**
 * Get all partner types from /partner_types
 */
export const getPartnerTypes = async (): Promise<PartnerType[]> => {
	try {
		const endpoint = '/partner_types';
		
		const response = await paginationService.getWithHandler(
			endpoint,
			undefined,
			100 // Get more items as partner types should be a relatively small list
		);

		const partnerTypeList: PartnerType[] = response?.member || response?.items || [];

		if (Array.isArray(partnerTypeList)) {
			return partnerTypeList;
		}

		return [];
	} catch (error: any) {
		console.error('Failed to fetch partner types:', error);
		throw new Error(error.message || 'Failed to fetch partner types');
	}
};

/**
 * Get all partner_lists with optional filtering and pagination
 */
export const getPartners = async (params: GetPartnersParams = {}): Promise<Partner[]> => {
	try {
		const {
			limit = 30,
			active,
		} = params;

		const queryParams: any = {};
		
		if (active !== undefined) {
			queryParams.active = active ? 1 : 0;
		}

		const endpoint = '/partner_lists';

		const queryString = Object.keys(queryParams).length > 0 
			? '?' + new URLSearchParams(queryParams).toString() 
			: '';
		const itemsPerPage = Math.min(limit, 30);
		const response = await paginationService.getWithHandler(
			`${endpoint}${queryString}`,
			undefined,
			itemsPerPage
		);

		const partnerList: RawPartnerListResponse[] = response?.member || response?.items || [];

		if (Array.isArray(partnerList)) {
			return partnerList.map(transformPartnerListResponse);
		}

		return [];
	} catch (error: any) {
		console.error('Failed to fetch partner_lists:', error);
		throw new Error(error.message || 'Failed to fetch partner_lists');
	}
};

/**
 * Get a single partner_list by ID
 */
export const getPartner = async (partnerId: IdResolvable): Promise<Partner> => {
	try {
		const response = apiService.responseHandler(
			await apiService.get(`/partner_lists/${resolvePartnerId(partnerId)}`)
		);

		const rawData: RawPartnerListResponse = response?.data || response;
		return transformPartnerListResponse(rawData);
	} catch (error: any) {
		console.error('Failed to fetch partner_list:', error);
		throw new Error(error.message || 'Failed to fetch partner_list');
	}
};

/**
 * Create a new partner_list
 */
export const createPartner = async (payload: CreatePartnerPayload): Promise<Partner> => {
	try {
		const response = apiService.responseHandler(
			await apiService.post('/partner_lists', payload)
		);

		const rawData: RawPartnerListResponse = response?.data || response;
		return transformPartnerListResponse(rawData);
	} catch (error: any) {
		console.error('Failed to create partner_list:', error);
		throw new Error(error.message || 'Failed to create partner_list');
	}
};

/**
 * Update an existing partner_list
 */
export const updatePartner = async (
	partnerId: IdResolvable,
	payload: Partial<UpdatePartnerPayload>
): Promise<Partner> => {
	try {
		const response = apiService.responseHandler(
			await apiService.patch(`/partner_lists/${resolvePartnerId(partnerId)}`, payload)
		);

		const rawData: RawPartnerListResponse = response?.data || response;
		return transformPartnerListResponse(rawData);
	} catch (error: any) {
		console.error('Failed to update partner_list:', error);
		throw new Error(error.message || 'Failed to update partner_list');
	}
};

/**
 * Delete a partner_list
 */
export const deletePartner = async (partnerId: IdResolvable): Promise<void> => {
	try {
		await apiService.delete(`/partner_lists/${resolvePartnerId(partnerId)}`);
	} catch (error: any) {
		console.error('Failed to delete partner_list:', error);
		throw new Error(error.message || 'Failed to delete partner_list');
	}
};

/**
 * Toggle partner_list active status
 */
export const togglePartnerStatus = async (partnerId: IdResolvable, active: boolean): Promise<Partner> => {
	try {
		const response = apiService.responseHandler(
			await apiService.patch(`/partner_lists/${resolvePartnerId(partnerId)}`, { active })
		);

		const rawData: RawPartnerListResponse = response?.data || response;
		return transformPartnerListResponse(rawData);
	} catch (error: any) {
		console.error('Failed to toggle partner_list status:', error);
		throw new Error(error.message || 'Failed to toggle partner_list status');
	}
};

/**
 * Partner List Workplace interface
 */
export interface PartnerListWorkplace {
	'@context': string;
	'@id': string;
	'@type': string;
	id: number;
	partnerList: string;
	workplace: string;
}

const transformPartnerListWorkplaceResponse = (raw: PartnerListWorkplace): PartnerListWorkplace => ({
	...raw,
	id: resolvePartnerId(raw),
});

/**
 * Create partner_list_workplace association
 */
export const createPartnerListWorkplace = async (payload: {
	partnerList: string;
	workplace: string;
}): Promise<PartnerListWorkplace> => {
	try {
		const response = apiService.responseHandler(
			await apiService.post('/partner_list_workplaces', payload)
		);
		const rawData: PartnerListWorkplace = response?.data || response;
		return transformPartnerListWorkplaceResponse(rawData);
	} catch (error: any) {
		console.error('Failed to create partner_list_workplace:', error);
		throw new Error(error.message || 'Failed to create partner_list_workplace');
	}
};

/**
 * Get partner_list_workplaces for a partner list
 */
export const getPartnerListWorkplaces = async (params: {
	partnerList?: IdResolvable;
	workplace?: IdResolvable;
}): Promise<PartnerListWorkplace[]> => {
	try {
		const queryParams: any = {};
		if (params.partnerList) queryParams.partnerList = resolvePartnerId(params.partnerList);
		if (params.workplace) queryParams.workplace = resolvePartnerId(params.workplace);

		const queryString = Object.keys(queryParams).length > 0
			? '?' + new URLSearchParams(queryParams).toString()
			: '';

		const response = await paginationService.getWithHandler(
			`/partner_list_workplaces${queryString}`,
			undefined,
			100
		);

		const workplaceList: PartnerListWorkplace[] = response?.member || response?.items || [];
		return Array.isArray(workplaceList)
			? workplaceList.map(transformPartnerListWorkplaceResponse)
			: [];
	} catch (error: any) {
		console.error('Failed to fetch partner_list_workplaces:', error);
		throw new Error(error.message || 'Failed to fetch partner_list_workplaces');
	}
};

/**
 * Delete partner_list_workplace association
 */
export const deletePartnerListWorkplace = async (id: IdResolvable): Promise<void> => {
	try {
		await apiService.delete(`/partner_list_workplaces/${resolvePartnerId(id)}`);
	} catch (error: any) {
		console.error('Failed to delete partner_list_workplace:', error);
		throw new Error(error.message || 'Failed to delete partner_list_workplace');
	}
};

// Export as service object for convenience
export const partnersService = {
	getCommonPartners,
	getPartnerTypes,
	getPartners,
	getPartner,
	createPartner,
	updatePartner,
	deletePartner,
	togglePartnerStatus,
	createPartnerListWorkplace,
	getPartnerListWorkplaces,
	deletePartnerListWorkplace,
	resolvePartnerId,
	searchToastRestaurants,
	formatToastRestaurantAddress,
};

export default partnersService;

